import { useState } from "react";
import { Sparkle } from "@phosphor-icons/react";
import { api, act } from "@/lib/api";
import { Panel, Field, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { StyleToggle, Chips, AssetCard, useAssets } from "@/components/studio/kit";

const CFG = {
  image: { aspects: ["1:1", "4:5", "16:9", "9:16", "3:2"], aspect: "1:1", ok: "Image generating (~30s)", ph: "e.g. A partner at a Melbourne accounting firm smiling at a laptop as client documents arrive automatically" },
  video: { aspects: ["16:9", "9:16", "1:1"], aspect: "16:9", ok: "Video rendering (1–4 min)", ph: "e.g. Slow cinematic push-in across a bright Sydney accounting office, a team celebrating finishing tax season early" },
};

export const MediaMaker = ({ kind }) => {
  const c = CFG[kind];
  const [f, setF] = useState({ brief: "", headline: "", style: "photo", aspect: c.aspect, duration: 5, audio: false });
  const [busy, setBusy] = useState(false);
  const { data, reload } = useAssets(kind);
  const set = (k, v) => setF({ ...f, [k]: v });
  const go = async () => {
    setBusy(true);
    const r = await act(() => api.post(`/studio/${kind}s`, f), c.ok);
    setBusy(false);
    if (r) { set("brief", ""); reload(); }
  };
  return (
    <div className="grid xl:grid-cols-[380px_1fr] gap-4">
      <Panel title={kind === "image" ? "New image" : "New short video"} className="self-start xl:sticky xl:top-4">
        <div className="space-y-4">
          <Field label="What should it show?"><Textarea rows={4} data-testid={`${kind}-brief-input`} value={f.brief} onChange={(e) => set("brief", e.target.value)} placeholder={c.ph} className="bg-black text-sm" /></Field>
          <Field label="Look"><StyleToggle value={f.style} onChange={(v) => set("style", v)} testid={`${kind}-style`} /></Field>
          <Field label="Format"><Chips value={f.aspect} onChange={(v) => set("aspect", v)} options={c.aspects} testid={`${kind}-aspect`} /></Field>
          {kind === "image" && <Field label="Headline on the image (optional)" hint="Leave blank for a clean visual"><Input data-testid="image-headline-input" maxLength={80} value={f.headline} onChange={(e) => set("headline", e.target.value)} className="bg-black" /></Field>}
          {kind === "video" && <>
            <Field label="Length"><Chips value={`${f.duration}s`} onChange={(v) => set("duration", parseInt(v, 10))} options={["5s", "10s"]} testid="video-duration" /></Field>
            <label className="flex items-center justify-between text-sm"><span>Ambient sound</span><Switch data-testid="video-audio-switch" checked={f.audio} onCheckedChange={(v) => set("audio", v)} /></label>
          </>}
          <Button data-testid={`${kind}-generate-btn`} disabled={busy || f.brief.trim().length < 8} onClick={go} className="w-full gap-2 bg-[#007AFF] hover:bg-[#3395FF]"><Sparkle weight="fill" /> {busy ? "Starting…" : `Generate ${kind}`}</Button>
          <div className="text-[11px] text-zinc-500">Uses your brand colours from Settings. Spend counts toward the charter media budget.</div>
        </div>
      </Panel>
      <div>
        {!data ? <div className="grid sm:grid-cols-2 gap-3">{[0, 1].map((k) => <div key={k} className="aspect-square rounded-md bg-[#121212] border border-border animate-pulse" />)}</div> : !data.length ? <Empty>No {kind}s yet — describe one on the left.</Empty>
          : <div className="grid sm:grid-cols-2 2xl:grid-cols-3 gap-3 items-start" data-testid={`${kind}-gallery`}>{data.map((a) => <AssetCard key={a.id} a={a} onChanged={reload} />)}</div>}
      </div>
    </div>
  );
};
